/**
 * Вкладка «Шаблоны» на `/posts`: слева области (общий + по сетям) с номером текущей версии, справа редактор
 * текущей версии («Сохранить как новую версию» — только при изменённом тексте), ниже история версий.
 * Клик по версии — её текст, разница с текущей (`lineDiff`), тексты, сгенерированные на ней, и «Вернуться».
 * Сохранение и возврат отвечают свежим `{current, history}` — кладём его в кэш, выбранную область не трогаем.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";

import { RetryImg } from "@/components/trends/PinGrid";
import { btn, btnPrimary, ErrorLine, input, Loading } from "@/components/trends/ui";
import { ApiFailure } from "@/lib/api";
import { lineDiff } from "@/lib/lineDiff";
import { thumbUrl, when } from "@/lib/media";
import { NETWORK_LABEL, type PostNetworkCode } from "@/lib/postPlan";
import { loadPromptVersion, loadPrompts, postsKeys, restorePrompt, savePrompt, type PromptsResult } from "@/lib/postsApi";

const CODE_TEXT: Record<string, string> = { UNCHANGED: "текст не изменился", EMPTY: "пустой шаблон" };

function failText(error: unknown, fallback = "Не удалось сохранить."): string {
  if (error instanceof ApiFailure) return CODE_TEXT[error.code] ?? error.message;
  return fallback;
}

const scopeLabel = (scope: string) => (scope === "base" ? "Общий" : (NETWORK_LABEL[scope as PostNetworkCode] ?? scope));

const DIFF_CLASS = { same: "text-fg-muted", del: "bg-err-bg text-err", add: "bg-ok-bg text-ok" } as const;
const DIFF_MARK = { same: "  ", del: "− ", add: "+ " } as const;

function VersionView({
  versionId,
  currentText,
  onRestore,
  restoring,
  restoreError,
}: {
  versionId: number;
  currentText: string;
  onRestore: () => void;
  restoring: boolean;
  restoreError: unknown;
}) {
  const q = useQuery({ queryKey: postsKeys.promptVersion(versionId), queryFn: () => loadPromptVersion(versionId) });
  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorLine error={q.error} />;
  const v = q.data;
  const diff = lineDiff(v.text ?? "", currentText);
  const same = diff.every((d) => d.kind === "same");
  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius-card)] border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium text-fg">Версия {v.number}</span>
        <button type="button" className={`${btn} ml-auto`} disabled={restoring || same} onClick={onRestore}>
          Вернуться к этой версии
        </button>
        {same ? <span className="text-[12px] text-fg-faint">это текущая версия</span> : null}
      </div>
      {restoreError ? <span className="text-[12px] text-err">{failText(restoreError, "Не удалось вернуть версию.")}</span> : null}
      <div className="flex flex-col gap-1">
        <span className="text-[11.5px] text-fg-faint">
          Разница: версия {v.number} → текущая (удалено красным, добавлено зелёным)
        </span>
        {same ? (
          <span className="text-[12.5px] text-fg-muted">Совпадает с текущей.</span>
        ) : (
          <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-[var(--radius-ui)] border border-border p-2 text-[12px]">
            {diff.map((d, i) => (
              <div key={i} className={DIFF_CLASS[d.kind]}>
                {DIFF_MARK[d.kind]}
                {d.text}
              </div>
            ))}
          </pre>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-[11.5px] text-fg-faint">Тексты, сгенерированные на этой версии: {v.gens.length}</span>
        {v.gens.length === 0 ? (
          <span className="text-[12.5px] text-fg-muted">Пока нет.</span>
        ) : (
          <div className="flex flex-col gap-2">
            {v.gens.map((g) => (
              <Link
                key={g.gen_id}
                to={`/posts/${g.plan_id}`}
                className="flex gap-2 rounded-[var(--radius-card)] border border-border p-1.5 transition-colors hover:border-accent"
              >
                {g.asset_path ? (
                  <RetryImg src={thumbUrl(g.asset_path)} alt="" className="h-16 w-12 shrink-0 rounded-[var(--radius-ui)] object-cover" />
                ) : null}
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="text-[11.5px] text-fg-faint">
                    {NETWORK_LABEL[g.network as PostNetworkCode] ?? g.network} · публикация №{g.plan_id} · {when(g.date_create)}
                  </span>
                  <span className="line-clamp-2 text-[12.5px] text-fg">{g.text ?? g.title ?? "—"}</span>
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function without<T>(map: Record<string, T>, key: string): Record<string, T> {
  const { [key]: _drop, ...rest } = map;
  return rest;
}

export function PromptsTab() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: postsKeys.prompts, queryFn: loadPrompts });
  const [scope, setScope] = useState("base");
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [notes, setNotes] = useState<Record<string, string>>({});
  const [picked, setPicked] = useState<number | null>(null);

  const put = (data: PromptsResult) => qc.setQueryData<PromptsResult>(postsKeys.prompts, data);
  const save = useMutation({
    mutationFn: (v: { text: string; note: string }) => savePrompt({ scope, text: v.text, note: v.note.trim() || null }),
    onSuccess: (data) => {
      put(data);
      qc.invalidateQueries({ queryKey: postsKeys.promptVersions });
      setDrafts((d) => without(d, scope));
      setNotes((n) => without(n, scope));
    },
  });
  const restore = useMutation({
    mutationFn: (id: number) => restorePrompt(id),
    onSuccess: (data) => {
      put(data);
      qc.invalidateQueries({ queryKey: postsKeys.promptVersions });
      setDrafts((d) => without(d, scope));
      setPicked(null);
    },
  });

  if (q.isPending) return <Loading />;
  if (q.isError) return <ErrorLine error={q.error} />;
  const { current, history } = q.data;
  const scopes = Object.keys(current);
  const cur = current[scope];
  const curText = cur?.text ?? "";
  const text = drafts[scope] ?? curText;
  const changed = text.trim() !== curText.trim();
  const note = notes[scope] ?? "";
  const pick = (s: string) => {
    setScope(s);
    setPicked(null);
    save.reset();
    restore.reset();
  };

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-[200px_1fr]">
      <div className="flex flex-row gap-1 md:flex-col" role="tablist" aria-label="Область шаблона">
        {scopes.map((s) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={s === scope}
            onClick={() => pick(s)}
            className={`flex items-center justify-between gap-2 rounded-[var(--radius-ui)] border px-3 py-1.5 text-left text-[13px] transition-colors ${
              s === scope ? "border-accent bg-bg-subtle text-fg" : "border-border text-fg-muted hover:border-accent"
            }`}
          >
            <span>{scopeLabel(s)}</span>
            <span className="text-[11.5px] text-fg-faint">v{current[s]?.number}</span>
          </button>
        ))}
      </div>
      <div className="flex min-w-0 flex-col gap-4">
        {!cur ? (
          <span className="text-[13px] text-fg-muted">Шаблона нет.</span>
        ) : (
          <div className="flex flex-col gap-2">
            <span className="text-[11.5px] text-fg-faint">
              {scopeLabel(scope)} · версия {cur.number} · {cur.author_name ?? "—"} · {when(cur.date_create)}
            </span>
            <textarea
              aria-label="Текст шаблона"
              className={`${input} min-h-64 w-full font-mono text-[12.5px]`}
              value={text}
              onChange={(e) => setDrafts((d) => ({ ...d, [scope]: e.target.value }))}
            />
            <div className="flex flex-wrap items-center gap-2">
              <input
                aria-label="Что поменяли"
                maxLength={300}
                className={`${input} min-w-64 flex-1`}
                placeholder="что поменяли"
                value={note}
                onChange={(e) => setNotes((n) => ({ ...n, [scope]: e.target.value }))}
              />
              <button
                type="button"
                className={btnPrimary}
                disabled={!changed || save.isPending}
                onClick={() => save.mutate({ text, note })}
              >
                Сохранить как новую версию
              </button>
            </div>
            {save.isError ? <span className="text-[12px] text-err">{failText(save.error)}</span> : null}
          </div>
        )}
        <div className="flex flex-col gap-1">
          <span className="text-[12.5px] font-medium text-fg">История</span>
          {(history[scope] ?? []).map((h) => (
            <button
              key={h.id}
              type="button"
              aria-pressed={picked === h.id}
              onClick={() => setPicked(picked === h.id ? null : h.id)}
              className={`flex flex-wrap items-baseline gap-x-3 rounded-[var(--radius-ui)] border px-3 py-1.5 text-left text-[12.5px] transition-colors ${
                picked === h.id ? "border-accent bg-bg-subtle" : "border-border hover:border-accent"
              }`}
            >
              <span className="font-medium text-fg">v{h.number}</span>
              <span className="text-fg-muted">{h.author_name ?? "—"}</span>
              <span className="text-fg-faint">{when(h.date_create)}</span>
              {h.note ? <span className="min-w-0 truncate text-fg-muted">{h.note}</span> : null}
              <span className="ml-auto text-fg-faint">генераций: {h.gens ?? 0}</span>
            </button>
          ))}
        </div>
        {picked !== null ? (
          <VersionView
            key={picked}
            versionId={picked}
            currentText={curText}
            onRestore={() => {
              if (changed && !window.confirm("Несохранённая правка шаблона пропадёт. Вернуть версию?")) return;
              restore.mutate(picked);
            }}
            restoring={restore.isPending}
            restoreError={restore.isError ? restore.error : null}
          />
        ) : null}
      </div>
    </div>
  );
}
